import React from 'react';
import Markdown from 'react-markdown';
import { styled } from 'styled-components';
import { Message } from '@dazzle/api-client';

import { Radius, Spacing } from 'Common/constants/theme';
import PersonCircleFill from 'Common/components/icons/PersonCircleFill';
import Link from 'Dazzle/components/icons/Link';

export const ConversationMessage = ({
  message,
  relativeTimestamp,
  onImageClick,
}: {
  message: Message;
  relativeTimestamp?: string;
  onImageClick: (imageUrl: string) => void;
}) => {
  const links = message.actions?.filter((action) => action.action_type === 'link').map((action) => action.url) ?? [];

  const openLink = (link: string) => {
    window.open(link, '_blank');
  }

  return (
    <>
      {relativeTimestamp && <TimestampContainer>{relativeTimestamp}</TimestampContainer>}

      <MessageAndLabelContainer>
        {message.concierge_name ? (
          <ConciergeLabel>
            <PersonCircleFill size={10} /> {message.concierge_name}
          </ConciergeLabel>
        ) : null}
        <MessageContainer $isUser={!message.is_user}>
          <MessageBubbleContainer $isUser={!message.is_user}>
            <MessageBubbleView>
              {message.image_urls?.map((url) => (
                <ConversationImage key={url} src={url} alt=" " onClick={() => onImageClick(url)} />
              ))}
              <Markdown>{message.content}</Markdown>
            </MessageBubbleView>
            {links.map(((link, i) => <LinkButton key={i} onClick={() => openLink(link)}><Link size={20}/>{link}</LinkButton>))}
          </MessageBubbleContainer>
        </MessageContainer>
      </MessageAndLabelContainer>
    </>
  );
};

const LinkButton = styled.button`
  text-overflow: ellipsis;
  max-width: 200px;
  overflow-x: hidden;
  text-wrap: nowrap;
  border: 1px solid black;
  margin: 5px 10px;
  border-radius: 6px;
  background: #666;
  color: white;

  svg {
    margin-right: 5px;
    margin-top: -3px;
  }
`;

const MessageAndLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ConciergeLabel = styled.div`
  display: flex;
  gap: 5px;
  margin: 0 ${Spacing.medium}px;
  font-size: 10px;
  justify-content: end;
  align-items: center;
`;

const MessageContainer = styled.div<{ $minHeight?: string, $isUser?: boolean }>`
  display: flex;
  justify-content: ${({ $isUser }) => $isUser ? 'flex-end' : 'flex-start'};
`;

const MessageBubbleContainer = styled.div<{ $isUser: boolean }>`
  max-width: ${props => props.$isUser ? '80%' : '100%'};
  margin: ${Spacing.xSmall}px ${Spacing.medium}px;
  background-color: ${props => props.$isUser ? '#008AFF' : '#E4E7EF'};
  color: ${({ $isUser }) => $isUser ? '#fff' : '#000'};
  border-radius: ${Radius.large}px ${Radius.large}px ${({ $isUser }) => $isUser ? `0 ${Radius.large}px` : `${Radius.large}px 0`};
`;

const MessageBubbleView = styled.div`
  font-size: 17px;
  padding: ${Spacing.compact}px;
  text-align: left;

  p {
    margin: 0;
  }
`;

const ConversationImage = styled.img`
  cursor: pointer;
  height: 100px;
  width: auto;
`;

const TimestampContainer = styled.div`
  font-size: 14px;
  display: flex;
  justify-content: center;
`;
